import {useCallback, useEffect, useState} from 'react';

import {useMutation} from '@apollo/react-hooks';

import type {EntitySummaryRating} from 'nutshell-graphql-types';
import type {
    LeadResearchMutation,
    LeadResearchMutationVariables,
    LeadResearchSummaryFragment as LeadResearchSummary,
} from 'nutshell-graphql-types';

import {useRateEntitySummary} from '../../../../../power-ai/timeline-summary/graphql/hooks/use-rate-entity-summary';

import LEAD_RESEARCH_MUTATION from '../mutations/lead-research.graphql';

type UseLeadResearchResult = {
    research: LeadResearchSummary | null | undefined;
    isLoading: boolean;
    error: string | null | undefined;
    runResearch: () => Promise<void>;
    rateResearch: (rating: EntitySummaryRating) => Promise<any>;
    isSavingRating: boolean;
};

export function useLeadResearch(
    leadId: string,
    existingResearch: LeadResearchSummary | null | undefined
): UseLeadResearchResult {
    const [research, setResearch] = useState<LeadResearchSummary | null | undefined>(
        existingResearch
    );
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setResearch(existingResearch);
        setError(null);
    }, [leadId, existingResearch]);

    const [mutate, {loading: isLoading}] = useMutation<
        LeadResearchMutation,
        LeadResearchMutationVariables
    >(LEAD_RESEARCH_MUTATION);

    const {rateEntitySummary, isSavingRating} = useRateEntitySummary();

    const runResearch = useCallback(async () => {
        setError(null);
        try {
            const result = await mutate({
                variables: {
                    input: {
                        leadId,
                    },
                },
            });

            const newResearchSummary =
                result.data &&
                result.data.leadResearch &&
                result.data.leadResearch.lead &&
                result.data.leadResearch.lead.research &&
                result.data.leadResearch.lead.research.summary;

            if (newResearchSummary) {
                setResearch(newResearchSummary);
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to research lead');
        }
    }, [leadId, mutate]);

    const rateResearch = useCallback(
        async (rating: EntitySummaryRating) => {
            if (!research || !research.id) {
                return Promise.resolve();
            }

            const result = await rateEntitySummary({entitySummaryId: research.id, rating});

            // Update local state with the new rating
            setResearch((prev) => (prev ? {...prev, userRating: rating} : prev));

            return result;
        },
        [research, rateEntitySummary]
    );

    return {
        research: research || existingResearch,
        isLoading,
        error,
        runResearch,
        rateResearch,
        isSavingRating,
    };
}
